import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import calc from '../../../utils/calc'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface ILocationGroupType {
  id: number
  name: string
}

interface IBusinessDateType {
  businessQuarter: number
  businessMonth: number
  businessWeek: number
  businessWeekOfMonth: number
}

interface IDataType {
  trendLocationGroupMetricValues: {
    nodes: (IBusinessDateType & {
      locationGroupId: number
      metricData: Record<
        string,
        {
          name: string
          unit: string
          value: number
        }
      >
    })[]
  }
}

interface ILocationCountDataType {
  trendLocationCount: {
    nodes: (IBusinessDateType & {
      locationGroupId: number
      locationCount: number
    })[]
  }
}

interface IReturnType {
  period: string
  locationGroupId: number
  value: number | null
}

const currentYearQuery = gql`
  query trendLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const twoYearQuery = gql`
  query trendTwoYearLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

// ENG-5756: trend_location_count counts each store once per group type, in the
// cohort active at the bucket end date (kit-core
// core/src/queries/customizedReport/farwest/trend_location_count.sql).
const locationCountQuery = gql`
  query trendLocationCount(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationCount(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        locationCount
      }
    }
  }
`

// TODO: should use hook to get the group ids and names
const MARKET = [
  {
    id: 36,
    name: 'fwrg',
  },
  {
    id: 37,
    name: 'gdpl',
  },
  {
    id: 38,
    name: 'trg',
  },
  {
    id: 39,
    name: 'mrg',
  },
  {
    id: 40,
    name: 'zrg',
  },
]

// TODO: should use hook to get the group ids and names
const CORE_NON_CORE = [
  {
    id: 89,
    name: 'core',
    displayName: 'Core',
  },
  {
    id: 90,
    name: 'nonCore',
    displayName: 'Non-Core',
  },
]

// TODO: should use hook to get the group ids and names
const COMP_NON_COMP = [
  {
    id: 57,
    name: 'comp',
    displayName: 'Comp',
  },
  {
    id: 56,
    name: 'nonComp',
    displayName: 'Non-Comp',
  },
]

const getPeriodLabel = (groupBy: string, item: IBusinessDateType) => {
  if (groupBy === 'business_week')
    return `P${item.businessMonth}W${item.businessWeekOfMonth}`
  else if (groupBy === 'business_quarter') return `Q${item.businessQuarter}`
  else return `P${item.businessMonth}`
}

const useFarwestTrendParams = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const yesterday = moment().subtract(1, 'day')
  const adjustedEndDate = moment(endDate, 'YYYY-MM-DD').isAfter(yesterday)
    ? yesterday.format('YYYY-MM-DD')
    : endDate

  const groupBy: string =
    variables.farwestTimeinterval?.value[0][0] || 'business_month'

  const dateList: string[] = ((groupBy: string) => {
    if (groupBy === 'business_week')
      return variables.date?.getCalendar('week', 'year')?.ids || []
    else if (groupBy === 'business_quarter') return ['Q1', 'Q2', 'Q3', 'Q4']
    else return variables.date?.getCalendar('period', 'year')?.ids || []
  })(groupBy)

  return { variables, startDate, endDate, adjustedEndDate, groupBy, dateList }
}

const buildFarwestSssBreakdownConfig = (locationGroups: ILocationGroupType[]) =>
  locationGroups.reduce((acc, item) => {
    acc[`${item.name}Current`] = 'percent'
    acc[`${item.name}TwoYears`] = 'percent'
    return acc
  }, {} as Record<string, string>)

const buildFarwestSssBreakdown = (locationGroups: ILocationGroupType[]) => {
  return () => {
    const {
      variables,
      startDate,
      endDate,
      adjustedEndDate,
      groupBy,
      dateList,
    } = useFarwestTrendParams()
    const locationGroupIds = locationGroups.map((l) => l.id)

    const metrics = variables.selectOptions?.orderMode?.value[0][0]

    const { data: currentData, loading: currentLoading } = useQuery<IDataType>(
      currentYearQuery,
      {
        variables: {
          iStartDate: startDate,
          iEndDate: adjustedEndDate,
          iGroupBy: groupBy,
          iFilter: {
            location_group_ids: locationGroupIds,
            metrics: [`${metrics}_percentage_diff_to_last_year`],
          },
        },
        skip: !startDate || !adjustedEndDate || !variables.date,
      },
    )

    const { data: twoYearData, loading: twoYearLoading } = useQuery<IDataType>(
      twoYearQuery,
      {
        variables: {
          iStartDate: startDate,
          iEndDate: adjustedEndDate,
          iGroupBy: groupBy,
          iFilter: {
            location_group_ids: locationGroupIds,
            metrics: [`${metrics}_percentage_diff_to_last_year`],
            yoy_offset: 1,
          },
        },
        skip: !startDate || !endDate || !variables.date,
      },
    )

    const loading = currentLoading || twoYearLoading

    return {
      data: useMemo((): IApiDataType => {
        if (
          !currentData?.trendLocationGroupMetricValues.nodes ||
          !twoYearData?.trendLocationGroupMetricValues.nodes ||
          !metrics
        )
          return null

        const toMetricData = (
          item: IDataType['trendLocationGroupMetricValues']['nodes'][number],
        ): IReturnType => ({
          period: getPeriodLabel(groupBy, item),
          locationGroupId: item.locationGroupId,
          value:
            item.metricData?.[`${metrics}_percentage_diff_to_last_year`].value *
            100,
        })

        const metricData =
          currentData.trendLocationGroupMetricValues.nodes.map(toMetricData)
        const twoYearMetricData =
          twoYearData.trendLocationGroupMetricValues.nodes.map(toMetricData)

        return dateList
          .map((date) => ({
            period: date,
            ...locationGroups.reduce((acc, l) => {
              const currentData = metricData.find(
                (d) => d.period === date && l.id === d.locationGroupId,
              )
              const twoYearData = twoYearMetricData.find(
                (d) => d.period === date && l.id === d.locationGroupId,
              )

              acc[`${l.name}Current`] = currentData?.value || null
              acc[`${l.name}TwoYears`] = calc(
                currentData?.value,
                '+',
                twoYearData?.value,
              ) as number
              return acc
            }, {} as Record<string, number | null>),
          }))
          .filter((d) => d.period)
      }, [currentData, twoYearData, dateList, groupBy, metrics]),
      loading,
    }
  }
}

const buildFarwestCohortCountConfig = (locationGroups: ILocationGroupType[]) =>
  locationGroups.reduce(
    (acc, item) => {
      acc[`${item.name}Count`] = 'number'
      acc[`${item.name}Percent`] = 'percent'
      acc[`${item.name}Tooltip`] = 'string'
      return acc
    },
    { totalCount: 'number' } as Record<string, 'number' | 'percent' | 'string'>,
  )

const buildFarwestCohortCount = (
  locationGroups: (ILocationGroupType & { displayName: string })[],
) => {
  return () => {
    const { variables, startDate, adjustedEndDate, groupBy, dateList } =
      useFarwestTrendParams()
    const locationGroupIds = locationGroups.map((l) => l.id)

    const { data, loading } = useQuery<ILocationCountDataType>(
      locationCountQuery,
      {
        variables: {
          iStartDate: startDate,
          iEndDate: adjustedEndDate,
          iGroupBy: groupBy,
          iFilter: {
            location_group_ids: locationGroupIds,
          },
        },
        skip: !startDate || !adjustedEndDate || !variables.date,
      },
    )

    return {
      data: useMemo((): IApiDataType => {
        const nodes = data?.trendLocationCount.nodes

        if (!nodes) return null

        const counts = nodes.map((item) => ({
          period: getPeriodLabel(groupBy, item),
          locationGroupId: item.locationGroupId,
          count: item.locationCount,
        }))

        return dateList.map((date) => {
          const periodCounts = counts.filter((c) => c.period === date)
          // why: return null (not 0) when the bucket has no rows at all - the
          // chart renderer trims trailing all-null rows so future / not-yet-
          // loaded buckets drop off the axis instead of rendering as 0%.
          const totalCount =
            periodCounts.length > 0
              ? periodCounts.reduce((sum, c) => sum + c.count, 0)
              : null

          return {
            period: date,
            totalCount,
            ...locationGroups.reduce((acc, l) => {
              // why: a cohort with no row in a bucket that has rows is a
              // confirmed zero, not missing data.
              const count =
                totalCount === null
                  ? null
                  : periodCounts.find((c) => c.locationGroupId === l.id)
                      ?.count ?? 0
              // why: the chart 'percent' format expects 0-100, not a ratio
              const percent =
                count === null || !totalCount
                  ? null
                  : (count / totalCount) * 100

              acc[`${l.name}Count`] = count
              acc[`${l.name}Percent`] = percent
              acc[`${l.name}Tooltip`] =
                count === null
                  ? l.displayName
                  : `${l.displayName} · ${count} of ${totalCount} stores`
              return acc
            }, {} as Record<string, number | string | null>),
          }
        })
      }, [data, groupBy, dateList]),
      loading,
    }
  }
}

export const fwMarketSssBreakdownConfig = {
  period: 'string',
  ...buildFarwestSssBreakdownConfig(MARKET),
} as const
export const fwCoreNoncoreSssBreakdownConfig = {
  period: 'string',
  ...buildFarwestSssBreakdownConfig(CORE_NON_CORE),
} as const
export const fwCoreNoncoreCohortCountConfig = {
  period: 'string',
  ...buildFarwestCohortCountConfig(CORE_NON_CORE),
} as const
export const fwCompNoncompCohortCountConfig = {
  period: 'string',
  ...buildFarwestCohortCountConfig(COMP_NON_COMP),
} as const

export const useFwMarketSssBreakdown = buildFarwestSssBreakdown(MARKET)
export const useFwCoreNoncoreSssBreakdown =
  buildFarwestSssBreakdown(CORE_NON_CORE)
export const useFwCoreNoncoreCohortCount =
  buildFarwestCohortCount(CORE_NON_CORE)
export const useFwCompNoncompCohortCount =
  buildFarwestCohortCount(COMP_NON_COMP)
